import {
  default as React,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  githubAuthApiRef,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useTheme } from '@mui/material/styles';
import { Octokit } from '@octokit/rest';

export const GithubSsoLogin = ({
  onChange,
  rawErrors = [],
  uiSchema,
}: FieldExtensionComponentProps<{
  githubOrg: string;
  email: string;
  name: string;
  login: string;
  token: string;
}>) => {
  const docsUrl = uiSchema?.['ui:options']?.docsUrl as string | undefined;
  const theme = useTheme();
  const githubAuth = useApi(githubAuthApiRef);
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const initialized = useRef(false);

  const [availableOrgs, setAvailableOrgs] = useState<string[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [samlAuthUrl, setSamlAuthUrl] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<{
    name: string;
    email: string;
    login: string;
    token: string;
  } | null>(null);

  const githubHost =
    config.getOptionalString('frontendPluginMicroservices.vcHost') ??
    'github.com';
  const nodeEnv = config.getOptionalString(
    'frontendPluginMicroservices.nodeEnv',
  );

  // LOCAL DEVELOPMENT ONLY (IDP-1050): bypass the GitHub OAuth popup with a PAT.
  // The shared GitHub App's OAuth callback URL does not support localhost, so the
  // OAuth flow cannot complete on a laptop. When a PAT is configured, use it instead
  // of githubAuth.getAccessToken().
  //
  // SAFETY: github.localDevToken is delivered to the frontend but is set ONLY in the
  // gitignored app-config.local.yaml on a developer's machine (see config.d.ts). It is
  // never present in app-config.yaml / app-config.development.yaml /
  // app-config.production.yaml or any deployed config, so this branch can only ever
  // activate locally. Its presence IS the "local mode" signal — deployed dev/prod
  // always fall through to the OAuth path below.
  const localDevToken = config.getOptionalString('github.localDevToken');
  const useLocalToken = Boolean(localDevToken);

  const fetchAndFilterOrgs = useCallback(async () => {
    try {
      setLoading(true);

      // Configured organizations from app-config (frontend-visible).
      // Defined in packages/app/config.d.ts with @visibility frontend.
      const orgsList =
        config.getOptionalStringArray('github.availableOrganizations') || [];

      // LOCAL DEVELOPMENT ONLY (IDP-1050): when a PAT is configured on localhost,
      // do NOT call the GitHub REST API from the browser. Direct browser→api.github.com
      // calls are fragile — org SAML enforcement, CORS, and token rejection can make
      // octokit.users.getAuthenticated() return 401 even for a token that is valid
      // server-side (verified via curl). Instead, populate the org dropdown from the
      // configured list and take the developer identity from the signed-in Backstage
      // session (Okta/guest). The PAT still flows downstream as host.token, where the
      // backend (/templates and repo creation) uses it with the GitHub App token fallback.
      if (useLocalToken) {
        if (orgsList.length === 0) {
          setError(
            'No organizations configured. Set github.availableOrganizations in app-config.local.yaml.',
          );
          setAvailableOrgs([]);
          setSelectedOrg('');
          return;
        }

        const localDefaultOrg = orgsList[0];
        setAvailableOrgs(orgsList);
        setSelectedOrg(localDefaultOrg);
        setError(null);

        // Developer identity from the Backstage session — no GitHub call needed.
        let localName = '';
        let localEmail = '';
        try {
          const profile = await identityApi.getProfileInfo();
          localName = profile.displayName || '';
          localEmail = profile.email || '';
        } catch (profileError) {
          // ignore — fall back to placeholders below
        }

        onChange({
          githubOrg: `${githubHost}/${localDefaultOrg}`,
          email: localEmail,
          name: localName || 'Local Developer',
          // `login` is a GitHub username (no spaces, lowercase). On the local
          // dev path we have a Backstage display name, not a GitHub handle, so
          // use a constant placeholder rather than the display name.
          login: 'local-dev',
          token: localDevToken!,
        });
        return;
      }

      // --- Deployed dev/prod: GitHub OAuth path ---
      const token = await githubAuth.getAccessToken([
        'repo',
        'read:org',
        'admin:org',
        'delete_repo',
      ]);
      const octokit = new Octokit({ auth: token });

      // Get user info
      const { data: user } = await octokit.users.getAuthenticated();

      // Debug logging in development
      if (nodeEnv === 'development') {
        // eslint-disable-next-line no-console
        console.log('Configured organizations from app-config:', orgsList);
      }

      // Query user's organization memberships
      let accessibleOrgs: string[] = [];
      try {
        const { data: userOrgs } =
          await octokit.rest.orgs.listForAuthenticatedUser({
            per_page: 100,
          });

        // Get list of orgs the user belongs to (member or admin)
        const memberOrgs = userOrgs.map(org => org.login.toLowerCase());

        // Debug logging in development
        if (nodeEnv === 'development') {
          // eslint-disable-next-line no-console
          console.log('User GitHub organizations:', memberOrgs);
        }

        // Cross-reference configured orgs with user's orgs
        accessibleOrgs = orgsList.filter(org =>
          memberOrgs.includes(org.toLowerCase()),
        );

        // Debug logging in development
        if (nodeEnv === 'development') {
          // eslint-disable-next-line no-console
          console.log(
            'Accessible organizations (intersection):',
            accessibleOrgs,
          );
        }
      } catch (orgError: any) {
        // If we can't get user orgs (e.g., SAML issue, 500 error), show all configured orgs
        // eslint-disable-next-line no-console
        console.warn(
          'Could not fetch user organizations, showing all configured orgs:',
          orgError,
        );

        // Show all configured organizations as fallback
        accessibleOrgs = orgsList;

        if (nodeEnv === 'development') {
          // eslint-disable-next-line no-console
          console.log(
            'Showing all configured organizations (could not verify membership):',
            accessibleOrgs,
          );
        }
      }

      // IDP-758: probe each org with teams.list to verify SAML authorization.
      // orgs.listForAuthenticatedUser returns orgs the user is a member of, but
      // doesn't check whether the OAuth token has SAML authorization for each org.
      // A 403 from teams.list means SAML enforcement is active and the token is not
      // authorized for that org — exclude it so users only see accessible orgs.
      const samlProbeResults = await Promise.all(
        accessibleOrgs.map(async org => {
          try {
            await octokit.rest.teams.list({ org, per_page: 1 });
            return { pass: true, ssoUrl: null as string | null };
          } catch (probeErr: any) {
            if (probeErr?.status === 403) {
              // Use x-github-sso header as the sole SAML discriminator — GitHub always
              // sets it on SAML-blocked 403s, so message text is not a reliable signal.
              const ssoHeader: string =
                (
                  probeErr?.response?.headers as
                    | Record<string, string>
                    | undefined
                )?.['x-github-sso'] ?? '';
              if (ssoHeader) {
                // Extract the authorization URL so the popup can authorize the token.
                const match = ssoHeader.match(/url=([^\s;]+)/);
                const rawUrl = match?.[1] ?? '';
                const ssoUrl = rawUrl.startsWith('https://github.com/')
                  ? rawUrl
                  : `https://github.com/orgs/${org}/sso`;
                return { pass: false, ssoUrl };
              }
              // 403 without x-github-sso = not a SAML issue (e.g. no team membership).
              // Org membership was already confirmed by listForAuthenticatedUser — keep it.
              return { pass: true, ssoUrl: null };
            }
            // Non-403 (network, 5xx) — keep the org in the list.
            return { pass: true, ssoUrl: null };
          }
        }),
      );
      const verifiedOrgs = accessibleOrgs.filter(
        (_, i) => samlProbeResults[i].pass,
      );

      if (verifiedOrgs.length === 0) {
        if (accessibleOrgs.length === 0) {
          setError(
            'No organizations configured. Please contact your administrator.',
          );
          setSamlAuthUrl(null);
        } else {
          // Check whether any blocked org has a SAML SSO URL (x-github-sso header present).
          const firstSsoUrl =
            samlProbeResults.find(r => !r.pass && r.ssoUrl)?.ssoUrl ?? null;
          setSamlAuthUrl(firstSsoUrl);
          if (firstSsoUrl) {
            setError(
              '⚠️ SAML authorization required. Click "Authorize on GitHub" below and complete the authorization, then return to this tab.',
            );
          } else {
            // 403 without x-github-sso = permissions problem, not SAML.
            setError(
              'Access denied to all organizations. Your account may lack the required GitHub permissions. Contact your administrator.',
            );
          }
        }
        setAvailableOrgs([]);
        setSelectedOrg('');
      } else {
        setAvailableOrgs(verifiedOrgs);
        const defaultOrg = verifiedOrgs[0];
        setSelectedOrg(defaultOrg);
        setError(null);
        setSamlAuthUrl(null);
      }

      // Get user email (existing logic)
      let userEmail = user.email || '';
      if (!userEmail) {
        try {
          const credentials = await identityApi.getCredentials();
          if (credentials.token) {
            const tokenParts = credentials.token.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              if (payload.email && !payload.sub?.includes('guest')) {
                userEmail = payload.email;
              }
            }
          }
        } catch (claimsError) {
          // Silently handle JWT parsing errors
        }
      }

      // Store user info + token in state so handleOrgChange can reuse both
      // without any async calls when the user switches organizations.
      setUserInfo({
        name: user.name || user.login || '',
        email: userEmail,
        login: user.login,
        token,
      });

      // Set initial form data with first org (always select first by default)
      const defaultOrg = verifiedOrgs.length > 0 ? verifiedOrgs[0] : '';
      const combinedHost = defaultOrg ? `${githubHost}/${defaultOrg}` : '';
      onChange({
        githubOrg: combinedHost,
        email: userEmail,
        name: user.name || user.login || '',
        login: user.login,
        token,
      });
    } catch (err: any) {
      if (nodeEnv === 'development') {
        // eslint-disable-next-line no-console
        console.error('GitHub SSO Login Error:', err);
      }

      // Provide more specific error messages
      if (err?.status === 401) {
        setError(
          'GitHub authentication failed. Please refresh the page and sign in again.',
        );
      } else if (err?.status === 403) {
        setError(
          'Access denied. You may need to authorize the Backstage app for your organizations.',
        );
      } else if (err?.status === 500) {
        setError(
          'GitHub API error. Please try refreshing the page. If the issue persists, contact support.',
        );
      } else {
        setError(
          'Failed to load GitHub organizations. Please try refreshing the page.',
        );
      }
    } finally {
      setLoading(false);
    }
  }, [
    githubAuth,
    identityApi,
    config,
    onChange,
    nodeEnv,
    githubHost,
    useLocalToken,
    localDevToken,
  ]);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      fetchAndFilterOrgs();
    }
  }, [fetchAndFilterOrgs]);

  const handleOrgChange = (event: SelectChangeEvent<string>) => {
    const newOrg = event.target.value || '';
    setSelectedOrg(newOrg);

    if (!useLocalToken) {
      // Synchronous — token was stored in userInfo at load time, no spinner needed.
      onChange({
        githubOrg: newOrg ? `${githubHost}/${newOrg}` : '',
        email: userInfo?.email ?? '',
        name: userInfo?.name ?? '',
        login: userInfo?.login ?? '',
        token: userInfo?.token ?? '',
      });
      setError(null);
      return;
    }

    // Local dev path: profile comes from Backstage session (async). Wrapped in an
    // IIFE so the outer function stays synchronous for the production path above.
    setLoading(true);
    (async () => {
      try {
        let localName = '';
        let localEmail = '';
        try {
          const profile = await identityApi.getProfileInfo();
          localName = profile.displayName || '';
          localEmail = profile.email || '';
        } catch {
          // ignore — fall back to placeholders below
        }
        onChange({
          githubOrg: newOrg ? `${githubHost}/${newOrg}` : '',
          email: localEmail,
          name: localName || 'Local Developer',
          login: 'local-dev',
          token: localDevToken!,
        });
        setError(null);
      } catch {
        setError(
          'Failed to switch organization. Please refresh the page and try again.',
        );
      } finally {
        setLoading(false);
      }
    })();
  };

  return (
    <FormControl
      fullWidth
      variant="outlined"
      error={!!error || rawErrors.length > 0}
      sx={{ mt: 1, mb: 1 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <InputLabel shrink sx={{ position: 'relative', transform: 'none' }}>
          GitHub Organization *
        </InputLabel>
        {loading && <CircularProgress size={18} sx={{ ml: 1 }} />}
      </Box>

      <Select
        value={selectedOrg}
        onChange={handleOrgChange}
        disabled={loading || availableOrgs.length === 0}
        required
        displayEmpty
        renderValue={value => {
          if (loading) {
            return (
              <span style={{ color: theme.palette.text.disabled }}>
                Loading organizations...
              </span>
            );
          }
          if (!value) {
            return (
              <span style={{ color: theme.palette.text.disabled }}>
                Select an organization
              </span>
            );
          }
          return String(value);
        }}
      >
        {availableOrgs.map(org => (
          <MenuItem key={org} value={org}>
            {org}
          </MenuItem>
        ))}
      </Select>

      {error && !samlAuthUrl && <FormHelperText error>{error}</FormHelperText>}

      {samlAuthUrl && (
        <Box
          sx={{
            mt: 1.5,
            p: 2,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'warning.main',
            bgcolor:
              theme.palette.mode === 'dark'
                ? 'rgba(255, 152, 0, 0.08)'
                : 'rgba(255, 152, 0, 0.06)',
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: 'warning.dark', mb: 0.75 }}
          >
            ⚠️ GitHub SSO not authorized
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            Your GitHub token has not been granted SSO access to any
            organization. To fix this:
          </Typography>
          <Typography
            component="ol"
            variant="body2"
            sx={{ color: 'text.secondary', m: 0, pl: 2, mb: 1.5 }}
          >
            <li>
              Go to{' '}
              <Link
                href="/settings/auth-providers"
                target="_blank"
                rel="noopener noreferrer"
              >
                Settings → Auth Providers
              </Link>
            </li>
            <li>
              Log out of the <strong>GitHub</strong> provider
            </li>
            <li>
              Return to this page, <strong>refresh it</strong>, then log in with
              GitHub — you will be prompted to grant SSO access during sign-in
            </li>
          </Typography>
          <Button
            variant="outlined"
            color="warning"
            size="small"
            onClick={() => window.location.reload()}
            sx={{ textTransform: 'none', mt: 0.5 }}
          >
            Already done? Refresh page
          </Button>
        </Box>
      )}
      {!error && (
        <FormHelperText sx={{ mt: 1, fontSize: '0.875rem' }}>
          First organization selected by default. Change if needed.
        </FormHelperText>
      )}
      {docsUrl && (
        <FormHelperText style={{ marginTop: 8 }}>
          <Link href={docsUrl} target="_blank" rel="noopener noreferrer">
            Learn more about this field
          </Link>
        </FormHelperText>
      )}
    </FormControl>
  );
};
